// src/components/eventos/components/diretor/GraficoDesempenho.js
import React, { useMemo } from 'react';

const GraficoDesempenho = ({ eventos }) => {
    const analise = useMemo(() => {
        const eventosConcluidos = eventos.filter(e => e.status === 'concluido');
        if (eventosConcluidos.length === 0) return { ultimos6Meses: [], porcentagemNoPrazo: 0, totalEventos: 0 };

        const agora = new Date();
        const seisMesesAtras = new Date(agora);
        seisMesesAtras.setMonth(seisMesesAtras.getMonth() - 6);

        const eventosRecentes = eventosConcluidos.filter(e => new Date(e.data) >= seisMesesAtras);

        const porMes = {};
        eventosRecentes.forEach(ev => {
            const d = new Date(ev.data);
            const chave = `${d.getFullYear()}-${d.getMonth()}`;
            if (!porMes[chave]) porMes[chave] = { mes: d.toLocaleDateString('pt-BR', { month: 'short' }), ano: d.getFullYear(), total: 0, noPrazo: 0 };
            porMes[chave].total++;
            const noPrazo = ev.tarefas?.every(t => {
                if (!t.horaFimReal || !t.horaFim) return true;
                const [hP, mP] = t.horaFim.split(':').map(Number);
                const [hR, mR] = t.horaFimReal.split(':').map(Number);
                return (hR * 60 + mR) <= (hP * 60 + mP + 5);
            });
            if (noPrazo) porMes[chave].noPrazo++;
        });

        const dadosPorMes = Object.values(porMes).sort((a, b) => a.ano !== b.ano ? a.ano - b.ano : ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'].indexOf(a.mes) - ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'].indexOf(b.mes));

        const totalNoPrazo = eventosRecentes.reduce((acc, ev) => {
            const ok = ev.tarefas?.every(t => {
                if (!t.horaFimReal || !t.horaFim) return true;
                const [hP, mP] = t.horaFim.split(':').map(Number);
                const [hR, mR] = t.horaFimReal.split(':').map(Number);
                return (hR * 60 + mR) <= (hP * 60 + mP + 5);
            });
            return acc + (ok ? 1 : 0);
        }, 0);

        return { ultimos6Meses: dadosPorMes, porcentagemNoPrazo: eventosRecentes.length > 0 ? Math.round((totalNoPrazo / eventosRecentes.length) * 100) : 0, totalEventos: eventosRecentes.length };
    }, [eventos]);

    if (analise.totalEventos === 0) {
        return (
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '48px 24px', textAlign: 'center', fontFamily: "'Inter', -apple-system, sans-serif", boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>📊</div>
                <p style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#64748b' }}>Sem Dados Suficientes</p>
                <p style={{ margin: 0, fontSize: '14px', color: '#94a3b8' }}>Complete alguns eventos para ver as análises de desempenho</p>
            </div>
        );
    }

    const pontColor = analise.porcentagemNoPrazo >= 90 ? '#2CCB90' : analise.porcentagemNoPrazo >= 70 ? '#d97706' : '#ef4444';
    const pontBg = analise.porcentagemNoPrazo >= 90 ? 'rgba(44,203,144,0.1)' : analise.porcentagemNoPrazo >= 70 ? 'rgba(217,119,6,0.1)' : 'rgba(239,68,68,0.08)';
    const pontBorder = analise.porcentagemNoPrazo >= 90 ? 'rgba(44,203,144,0.2)' : analise.porcentagemNoPrazo >= 70 ? 'rgba(217,119,6,0.2)' : 'rgba(239,68,68,0.2)';
    const maxVal = Math.max(...analise.ultimos6Meses.map(m => m.total), 1);

    return (
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', fontFamily: "'Inter', -apple-system, sans-serif", boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
                <div>
                    <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Análise de Desempenho</h3>
                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Últimos 6 meses • {analise.totalEventos} eventos analisados</p>
                </div>
                <span style={{ padding: '6px 14px', borderRadius: '100px', background: pontBg, border: `1px solid ${pontBorder}`, color: pontColor, fontSize: '14px', fontWeight: 700 }}>
                    ✓ {analise.porcentagemNoPrazo}% no prazo
                </span>
            </div>

            {/* Barras */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {analise.ultimos6Meses.map((mes, i) => {
                    const pct = mes.total > 0 ? Math.round((mes.noPrazo / mes.total) * 100) : 0;
                    const vol = (mes.total / maxVal) * 100;
                    const barColor = pct >= 90 ? '#2CCB90' : pct >= 70 ? '#d97706' : '#ef4444';
                    return (
                        <div key={i}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', minWidth: '52px', textTransform: 'capitalize' }}>{mes.mes}/{mes.ano.toString().slice(2)}</span>
                                    <span style={{ padding: '2px 8px', borderRadius: '6px', background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)', color: '#2563eb', fontSize: '11px', fontWeight: 600 }}>{mes.total} eventos</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ padding: '2px 8px', borderRadius: '6px', background: `${barColor}18`, border: `1px solid ${barColor}35`, color: barColor, fontSize: '11px', fontWeight: 600 }}>{pct}% no prazo</span>
                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>({mes.noPrazo}/{mes.total})</span>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                <div style={{ height: '10px', borderRadius: '100px', background: '#e2e8f0', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${vol}%`, borderRadius: '100px', background: 'linear-gradient(90deg, #2563eb, #2DB8E1)', transition: 'width 0.6s ease' }} />
                                </div>
                                <div style={{ height: '10px', borderRadius: '100px', background: '#e2e8f0', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${pct}%`, borderRadius: '100px', background: `linear-gradient(90deg, ${barColor}, ${barColor}cc)`, transition: 'width 0.6s ease' }} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Legenda */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '14px', height: '10px', borderRadius: '3px', background: 'linear-gradient(90deg, #2563eb, #2DB8E1)', flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Volume de eventos</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '14px', height: '10px', borderRadius: '3px', background: '#2CCB90', flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Taxa de pontualidade</span>
                </div>
            </div>
        </div>
    );
};

export default GraficoDesempenho;
